import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { TestResult } from '../models/test-result.model';
import { BackendChannelService } from './backend-channel.service';

@Injectable({
  providedIn: 'root',
})
export class LiveRealtimeService {
  private socket$: WebSocketSubject<any>;
  private URL = '';
  private RECONNECT_INTERVAL = 30000;
  private MAX_RETRY_ATTEMPTS = 5;
  private attemptCount = 0;
  private lastSentMessage: TrackTestIds | null = null;

  constructor(private backendChannel: BackendChannelService) {
    const url = this.backendChannel.getBaseUrl(this.backendChannel.$current.value) + '/realtime/status-updates';
    this.URL = url.replace('http', 'ws');
  }

  public connect(): Observable<Partial<TestResult>> {
    return new Observable(observer => {
      const setupConnection = () => {
        this.socket$ = webSocket({
          url: this.URL,
          openObserver: {
            next: () => {
              this.attemptCount = 0; // reset the attempt count if connection was successful

              if (this.lastSentMessage) {
                this.socket$.next(this.lastSentMessage);
              }
            },
          },
        });

        this.socket$.subscribe({
          next: msg => {
            observer.next(msg);
          },
          error: err => {
            if (err.code === 1006 && ++this.attemptCount <= this.MAX_RETRY_ATTEMPTS) {
              console.warn(`WebSocket error (attempt ${this.attemptCount}):`, err);
              setTimeout(setupConnection, this.RECONNECT_INTERVAL);
            } else {
              observer.error(err);
            }
          },
          complete: () => {
            console.warn('WebSocket connection closed.');
            observer.complete();
          },
        });
      };

      setupConnection();

      return () => {
        this.socket$?.complete();
        this.socket$?.unsubscribe();
      };
    });
  }

  public sendMessage(msg: TrackTestIds) {
    this.lastSentMessage = msg;

    if (!this.socket$ || this.socket$.closed) {
      this.connect().subscribe({
        error: err => {
          console.error('Reconnection failed:', err);
        },
      });
    } else {
      this.socket$.next(msg);
    }
  }

  public close() {
    this.socket$?.complete();
    this.socket$?.unsubscribe();
    this.socket$ = null;
    this.lastSentMessage = null;
  }
}

interface TrackTestIds {
  trackedTestIds: number[];
}
